import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X, ChevronDown, Leaf } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import Dropdown from './Dropdown';

interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

export default function Navigation({ currentPage, onPageChange }: NavigationProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const mainPages = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'development', label: 'Development' },
    { id: 'community', label: 'Community' },
    { id: 'updates', label: 'Updates' },
  ];

  const morePages = [
    { id: 'history', label: 'Our History' },
    { id: 'data-explorer', label: 'Data Explorer' },
    { id: 'types', label: 'Types of Sugar' },
    { id: 'gallery', label: 'Photo Gallery' },
    { id: 'contact', label: 'Contact' },
  ];

  const memberCompanies = [
    { name: 'APAMO - Mozambique', url: 'https://apamo.co.mz/' },
    { name: 'Belize Sugar Industries', url: 'https://asr-group.com/belize-sugar-industries' },
    { name: 'Eswatini Sugar Association', url: 'https://esa.co.sz/' },
    { name: 'Illovo Sugar Africa', url: 'https://www.illovosugarafrica.com/' },
    { name: 'Mauritius Sugar Syndicate', url: 'https://mauritiussugar.mu/' },
    { name: 'South African Sugar Association', url: 'https://sasa.org.za/' },
    { name: 'Fiji Sugar Corporation', url: 'https://www.sugarsoffiji.com/' },
    { name: 'Dominican Republic', url: 'https://www.inazucar.gov.do/' },
  ];

  return (
    <>
      {/* Spacer to prevent content jump */}
      {currentPage !== 'home' && <div className="h-24"></div>}
      
      <nav className="fixed top-6 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-300">
        <div className={`
          backdrop-blur-xl border border-white/20 rounded-2xl transition-all duration-300 shadow-lg
          ${isScrolled 
            ? 'bg-white/30 shadow-2xl scale-95' 
            : 'bg-white/80 shadow-xl'
          }
        `}>
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center px-10 py-5 space-x-8">
            <button
              onClick={() => onPageChange('home')}
              className="flex-shrink-0 flex items-center space-x-3 hover:opacity-80 transition-opacity mr-4 action"
            >
              <div className="relative">
                <div className="w-10 h-10 bg-lime-500 rounded-xl flex items-center justify-center shadow-md">
                  <Leaf className="w-5 h-5 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-golden-harvest rounded-full border-2 border-white"></div>
              </div>
              <div className="hidden xl:block">
                <div className="font-bold text-lg text-forest-green">ACP/LDC</div>
                <div className="text-xs text-sugar-cane-light font-medium -mt-1">Sugar Industries</div>
              </div>
            </button>

            {/* Main Navigation Links */}
            <div className="flex items-center space-x-9">
              {mainPages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => onPageChange(page.id)}
                  className={`relative px-3 py-2 text-md font-medium transition-all duration-200 action ${
                    currentPage === page.id
                      ? 'text-green-600'
                      : 'text-gray-900 hover:text-lime-800 hover:text-bold'
                  }`}
                >
                  {page.label}
                  {currentPage === page.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-leaf-green rounded-full"></div>
                  )}
                </button>
              ))}
            </div>
            
            {/* Dropdown Menus */}
            <div className="flex items-center space-x-4">
              <Dropdown label="More" items={morePages} onSelect={onPageChange} />

              <Button 
                onClick={() => onPageChange('contact')}
                size="lg"
                className="bg-leaf-green hover:bg-sugar-cane text-white px-6 py-2 rounded-xl font-medium shadow-md hover:shadow-lg transition-all duration-200 action font-semibold"
              >
                Contact
              </Button>
            </div>
          </div>

          {/* Mobile Navigation Header */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3">
            {/* Mobile Logo */}
            <button
              onClick={() => onPageChange('home')}
              className="flex-shrink-0 flex items-center space-x-2 hover:opacity-80 transition-opacity"
            >
              <div className="relative">
                <div className="w-8 h-8 bg-leaf-green rounded-lg flex items-center justify-center shadow-md">
                  <Leaf className="w-4 h-4 text-white" />
                </div>
                <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-golden-harvest rounded-full border border-white"></div>
              </div>
              <div>
                <div className="font-bold text-sm text-forest-green">ACP/LDC</div>
                <div className="text-xs text-sugar-cane-light font-medium -mt-0.5">Sugar Industries</div>
              </div>
            </button>

            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-leaf-green p-2"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-2 bg-white/95 backdrop-blur-xl border border-white/20 rounded-2xl shadow-2xl p-4 w-80 max-w-[90vw]">
            <div className="space-y-2">
              {[...mainPages, ...morePages].map((page) => (
                <button
                  key={page.id}
                  onClick={() => {
                    onPageChange(page.id);
                    setIsMenuOpen(false);
                  }}
                  className={`block w-full text-left px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                    currentPage === page.id
                      ? 'text-leaf-green bg-leaf-green/10'
                      : 'text-gray-700 hover:text-leaf-green hover:bg-leaf-green/5'
                  }`}
                >
                  {page.label}
                </button>
              ))}
            </div>
            
            <div className="border-t border-gray-200 pt-4 mt-4">
              <p className="px-4 py-2 text-xs text-sugar-cane uppercase tracking-wide font-semibold">Member Companies</p>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {memberCompanies.map((company, index) => (
                  <a
                    key={index}
                    href={company.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-4 py-2 text-xs text-gray-600 hover:text-leaf-green hover:bg-leaf-green/5 rounded-lg transition-colors"
                  >
                    {company.name}
                  </a>
                ))}
              </div>
            </div>
            
            <div className="pt-4 mt-4 border-t border-gray-200">
              <Button 
                onClick={() => {
                  onPageChange('contact');
                  setIsMenuOpen(false);
                }}
                className="w-full bg-leaf-green hover:bg-sugar-cane text-white py-2.5 rounded-xl font-medium"
                size="sm"
              >
                Get in Touch
              </Button>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}